/**
 * Skeleton — what a screen shows while its data is on the way.
 *
 * A spinner blanks the screen and then everything jumps into place; a
 * skeleton keeps the layout still and the eye where the content will land.
 * The audit counted 125 files with a spinner and 31 with a skeleton. These
 * five shapes cover almost every list, grid and stat strip in the portal:
 *
 *   <SkeletonPage />                      — header + stats + rows (a whole screen)
 *   <SkeletonStats count={4} />           — the stat strip under a page header
 *   <SkeletonRows rows={6} />             — a table or list
 *   <SkeletonCards count={6} />           — a product / customer card grid
 *   <SkeletonText lines={3} />            — a paragraph
 *   <Skeleton className="h-4 w-32" />     — one bar, when nothing above fits
 *
 * `motion-reduce:animate-none` — the pulse respects prefers-reduced-motion.
 * `aria-hidden` — screen readers get the page's own loading announcement, not
 * a hundred grey rectangles.
 */
import React from 'react';

export const Skeleton = ({ className = '', style }) => (
  <div aria-hidden="true" className={`animate-pulse motion-reduce:animate-none rounded-md bg-gray-200/70 ${className}`} style={style} />
);

export const SkeletonText = ({ lines = 3, className = '' }) => (
  <div aria-hidden="true" className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} className="h-3.5" style={{ width: `${i === lines - 1 ? 55 : 90 - (i % 3) * 8}%` }} />
    ))}
  </div>
);

export const SkeletonStats = ({ count = 4, className = '' }) => (
  <div aria-hidden="true" className={`grid grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-2xl border border-line bg-white p-5">
        <Skeleton className="h-3 w-24 mb-4" />
        <Skeleton className="h-7 w-20" />
      </div>
    ))}
  </div>
);

export const SkeletonRows = ({ rows = 6, className = '' }) => (
  <div aria-hidden="true" className={`rounded-2xl border border-line bg-white divide-y divide-line overflow-hidden ${className}`}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-3.5">
        <Skeleton className="h-9 w-9 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton className="h-3.5" style={{ width: `${45 + (i % 4) * 10}%` }} />
          <Skeleton className="h-3 w-1/4" />
        </div>
        <Skeleton className="h-4 w-16 hidden sm:block" />
        <Skeleton className="h-6 w-20 rounded-full hidden md:block" />
      </div>
    ))}
  </div>
);

export const SkeletonCards = ({ count = 6, className = '' }) => (
  <div aria-hidden="true" className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-2xl border border-line bg-white overflow-hidden">
        <Skeleton className="aspect-[4/5] rounded-none" />
        <div className="p-3 space-y-2">
          <Skeleton className="h-3.5 w-3/4" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
    ))}
  </div>
);

/** A whole screen: the page header's shape, a stat strip, then rows. */
export const SkeletonPage = ({ stats = 4, rows = 6, className = '' }) => (
  <div aria-hidden="true" className={className}>
    <Skeleton className="h-3 w-20 mb-3" />
    <Skeleton className="h-9 w-64 mb-2" />
    <Skeleton className="h-4 w-96 max-w-full mb-8" />
    {stats > 0 && <SkeletonStats count={stats} className="mb-6" />}
    <SkeletonRows rows={rows} />
  </div>
);

export default Skeleton;
